import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import { LayoutGuest } from "../../boxes/LayoutGuest/LayoutGuest";
import { authService } from "../../services/AuthService";
import classes from "./Login.module.scss";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { Input } from "../../shared/Input/Input";
import { useFormTwoFactorLogin } from "./hooks/useFormTwoFactorLogin";
import { TWO_FACTOR_TYPES } from "../../consts/api";
import { InputError } from "../../shared/InputError/InputError";
import { Button } from "../../shared/Button/Button";
import { CircularProgress } from "../../shared/CircularProgress/CircularProgress";
import { useTwoFactorLogin } from "./hooks/useTwoFactorLogin";
import { Checkbox } from "../../shared/Checkbox/Checkbox";
import { AlternativeOtp } from "./shared/AlternativeOtp";
import { useResendOtp } from "./hooks/useResendOtp";
import { LOCAL_STORAGE_KEYS } from "../../consts/localStorage";
import { AlertBox } from "../../shared/AlertBox/AlertBox";

function TwoFactorLogin({ history }) {
  const { twoFactorType, otpSentTo } = authService.getPreLoginData();

  const { tCommon } = useAppTranslation.Common();
  const { tAuth } = useAppTranslation.Auth();
  const { verify, isVerifying } = useTwoFactorLogin();
  const [isAlternativeOpen, setIsAlternativeOpen] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const {
    form,
    isFormValid,
    onFormValueBlur,
    onFormValueChange,
    submit,
    hasError,
    errors,
    setFieldValue,
  } = useFormTwoFactorLogin({
    onSubmit: (values, handlers) => verify(values, twoFactorType, handlers),
  });

  const { initiate: resendOtp, isLoading: isLoadingResend } = useResendOtp();

  const withOtpResend =
    twoFactorType === TWO_FACTOR_TYPES.email ||
    twoFactorType === TWO_FACTOR_TYPES.sms;

  const securityQuestion = authService.getPreLoginData().securityQuestion;

  const codePlaceholderByType = {
    [TWO_FACTOR_TYPES.email]: tAuth("label.enterOtp"),
    [TWO_FACTOR_TYPES.sms]: tAuth("label.enterOtp"),
    [TWO_FACTOR_TYPES.google]: tAuth("label.enterToken"),
  };

  useEffect(() => {
    if (!twoFactorType) {
      history.goBack();
    }
  }, [history]);

  useEffect(() => {
    const savedUntil = localStorage.getItem(
      LOCAL_STORAGE_KEYS.twoFactorResendUntil,
    );
    if (savedUntil) {
      const diff = Math.floor((+savedUntil - Date.now()) / 1000);
      if (diff > 0) {
        setResendCooldown(diff);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.twoFactorResendUntil);
      }
    }
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.twoFactorResendUntil);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendCooldown]);

  const handleResend = async () => {
    await resendOtp();
    const until = Date.now() + 30 * 1000;
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.twoFactorResendUntil,
      String(until),
    );
    setResendCooldown(30);
  };

  return (
    <LayoutGuest>
      <div className={classes.root}>
        <h1 className={classes.header}>{tAuth("twoFactor.header")}</h1>
        <AlertBox size="small" variant="success" className={classes.alert}>
          {tAuth("twoFactor.enterCode", { to: otpSentTo })}
        </AlertBox>
        <div className={classes.form}>
          <div>
            <Input
              autoFocus
              type="code"
              name="code"
              value={form.code}
              onChange={onFormValueChange}
              onBlur={onFormValueBlur}
              isError={hasError("code")}
              isDisabled={isVerifying}
              placeholder={codePlaceholderByType[twoFactorType]}
            />
            {hasError("code") && <InputError>{errors.code}</InputError>}
          </div>
          {!!withOtpResend && (
            <Button
              onClick={handleResend}
              variant="text"
              fullWidth
              isDisabled={isLoadingResend || resendCooldown > 0}
            >
              {resendCooldown > 0
                ? `${tAuth(
                    "twoFactor.alternativeOtp.resendIn",
                  )} ${resendCooldown}s`
                : tAuth("twoFactor.alternativeOtp.resendOtp")}
            </Button>
          )}
          <Button
            fullWidth
            onClick={submit}
            isDisabled={!isFormValid || isVerifying}
            leftAdornment={
              isVerifying ? (
                <CircularProgress size="small" color="white" />
              ) : undefined
            }
          >
            {tCommon("label.continue")}
          </Button>
          <Checkbox
            label={tAuth("twoFactor.trustDevice")}
            isChecked={form.trustDevice}
            onChange={() => setFieldValue("trustDevice", !form.trustDevice)}
          />
          {!!securityQuestion && (
            <button
              className={classes.anotherWay}
              onClick={() => setIsAlternativeOpen(true)}
            >
              {tAuth("twoFactor.anotherWay")}
            </button>
          )}
        </div>
      </div>
      {isAlternativeOpen && (
        <AlternativeOtp onClose={() => setIsAlternativeOpen(false)} />
      )}
    </LayoutGuest>
  );
}

TwoFactorLogin.propTypes = {
  history: PropTypes.object,
};

export default withRouter(TwoFactorLogin);
